/**
 * Header — brand + access-aware nav + the account menu. Studio appears for
 * every signed-in platform account; Admin links remain role/allowlist gated.
 * The user chip itself (avatar + handle + dropdown) lives in
 * {@link AccountMenu}.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../services/context.js';
import { canCreate, isSuperadmin, isStatsViewer } from '../auth/model.js';
import { AccountMenu } from './AccountMenu.js';

export function Header() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!mobileRootRef.current?.contains(event.target as Node)) setMobileOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileOpen]);

  const navLinks = () => (
    <>
      <NavLink to="/gallery" onClick={() => setMobileOpen(false)}>
        Gallery
      </NavLink>
      <NavLink to="/explore" onClick={() => setMobileOpen(false)}>
        Explore
      </NavLink>
      <NavLink to="/leaderboard" onClick={() => setMobileOpen(false)}>
        Leaderboard
      </NavLink>
      {canCreate(user) && (
        <NavLink to="/studio" onClick={() => setMobileOpen(false)}>
          Studio
        </NavLink>
      )}
      {isSuperadmin(user) && (
        <NavLink to="/admin" onClick={() => setMobileOpen(false)}>
          Admin
        </NavLink>
      )}
      <NavLink to="/about" onClick={() => setMobileOpen(false)}>
        About us
      </NavLink>
      <NavLink to="/stats" onClick={() => setMobileOpen(false)}>
        Stats
      </NavLink>
      <NavLink to="/deej" onClick={() => setMobileOpen(false)}>
        Deej
      </NavLink>
      {isStatsViewer(user) && (
        <NavLink to="/admin/stats" onClick={() => setMobileOpen(false)}>
          Admin Stats
        </NavLink>
      )}
    </>
  );

  return (
    <header className="header">
      <div className="container header__inner">
        <NavLink to="/" className="brand">
          d<span className="brand__mark">jgg</span>
        </NavLink>
        <nav className="header__nav" aria-label="Primary navigation">
          {navLinks()}
        </nav>
        <div className="header__spacer" />
        <Link to="/studio/create" className="btn btn--primary header__create" onClick={() => setMobileOpen(false)}>
          Create game
        </Link>
        <div className="header__mobile" ref={mobileRootRef}>
          <button
            type="button"
            className="header__menu-toggle"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-controls="mobile-primary-navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((was) => !was)}
            data-testid="mobile-nav-toggle"
          >
            <span aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
          </button>
          {mobileOpen && (
            <nav
              id="mobile-primary-navigation"
              className="header__mobile-nav"
              aria-label="Mobile primary navigation"
              data-testid="mobile-nav"
            >
              {navLinks()}
            </nav>
          )}
        </div>
        <AccountMenu />
      </div>
    </header>
  );
}
